/**
 * @file command-search-filter.ts
 * @description Intelligent search matching & scoring for the CommandSearch palette.
 *              Supports multi-word tokens, parent hierarchy matching, typo tolerance,
 *              and singular/plural normalization.
 */

/**
 * Computes Levenshtein distance between two strings for typo tolerance.
 */
export function levenshteinDistance(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  const matrix: number[][] = [];
  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1,     // insertion
          matrix[i - 1][j] + 1,     // deletion
        );
      }
    }
  }

  return matrix[b.length][a.length];
}

/**
 * Normalizes text for search indexing and querying.
 */
export function normalizeSearchText(str: string): string {
  return (str || '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Custom cmdk filter function:
 * - Multi-token matching across ancestor categories, leaf labels, and routes
 * - Word-order independent ("school roles" matches "Roles & Permissions > School App")
 * - Typo tolerance for words >= 4 chars ("permision" matches "permission")
 * - Singular / plural normalization ("role" matches "roles")
 * - Stop-word handling ("and", "&")
 *
 * Returns 0 if item should be hidden, or score between 0.1 and 1.0 for ranking.
 */
export function commandSearchFilter(
  value: string,
  search: string,
  keywords?: string[],
): number {
  if (!search || !search.trim()) return 1;

  const query = normalizeSearchText(search);
  if (!query) return 1;

  const target = normalizeSearchText(`${value} ${(keywords || []).join(' ')}`);

  // Direct continuous substring match gets top rank
  if (target.includes(query)) {
    return 1;
  }

  const queryTokens = query.split(' ').filter(Boolean);
  if (queryTokens.length === 0) return 1;

  // Stop words that are optional if the query has other meaningful words
  const stopWords = new Set(['and', 'or', 'the', 'in', 'of', 'for', 'to', 'a', 'an']);
  const nonStopTokens = queryTokens.filter((t) => !stopWords.has(t));
  const tokensToMatch = nonStopTokens.length > 0 ? nonStopTokens : queryTokens;

  const targetWords = target.split(' ').filter(Boolean);
  let totalScore = 0;

  for (const token of tokensToMatch) {
    let bestTokenScore = 0;

    for (const word of targetWords) {
      // 1. Exact word match
      if (word === token) {
        bestTokenScore = Math.max(bestTokenScore, 1.0);
        break;
      }

      // 2. Singular / plural match
      const stemToken = token.replace(/e?s$/, '');
      const stemWord = word.replace(/e?s$/, '');
      if (stemToken && stemWord && stemToken === stemWord) {
        bestTokenScore = Math.max(bestTokenScore, 0.95);
        continue;
      }

      // 3. Prefix match (e.g. "perm" in "permissions")
      if (word.startsWith(token)) {
        bestTokenScore = Math.max(bestTokenScore, 0.85);
        continue;
      }

      // 4. Substring match
      if (word.includes(token) || (token.length >= 3 && token.includes(word))) {
        bestTokenScore = Math.max(bestTokenScore, 0.75);
        continue;
      }

      // 5. Typo tolerance via Levenshtein distance
      if (token.length >= 4) {
        const dist = levenshteinDistance(token, word);
        if (dist === 1) {
          bestTokenScore = Math.max(bestTokenScore, 0.8);
          continue;
        }
        if (token.length >= 7 && dist === 2) {
          bestTokenScore = Math.max(bestTokenScore, 0.6);
          continue;
        }
      }
    }

    // Every required token must match something in the target item
    if (bestTokenScore === 0) {
      return 0;
    }

    totalScore += bestTokenScore;
  }

  const avgScore = totalScore / tokensToMatch.length;
  return Math.min(0.99, Math.max(0.1, avgScore));
}
